import { Box, Grid, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import * as React from 'react';

import UserTextInput from 'components/UserInput/UInput';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  useFeedbackQuestion,
  useMutationSaveLunchFeedbackAnswer
} from 'redux/reducers/user/dashboard/tms-feedback';
import RadioGroup from './RadioGroupComponent';

interface ILunchFeedbackModal {
  openFeedbackModal: any;
  handleCloseFeedbackModal: () => void;
  routine: {
    id: number;
    date: string;
  };
}

interface IFeedbackAns {
  daily_session: string;
  routine_feedback_que_id: string;
  feedback_question_id: string;
  feedback_point?: string;
  feedback_answer?: string;
  feedback_date: string;
}

const LunchFeedbackModal = ({
  openFeedbackModal,
  handleCloseFeedbackModal,
  routine
}: ILunchFeedbackModal) => {
  const { id, date } = routine;
  const routineId = id;

  const { data: Questions, isLoading: isLoadingFeedback } = useFeedbackQuestion(id.toString() ?? '', 'lunch');

  const lunchFeedbackQuestionList = React.useMemo(
    () => Questions?.flatMap((fq) => fq.feedback_question_details) ?? [],
    [Questions]
  );

  const { control, reset, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      feedbackAnswers: [] as IFeedbackAns[]
    }
  });

  const feedbackAns = watch('feedbackAnswers');

  const { mutateAsync, isLoading } = useMutationSaveLunchFeedbackAnswer();

  const onSubmitHandler = async () => {
    try {
      await mutateAsync(feedbackAns);
      toast.success('Training Feedback Created Successfully');
    } catch (error: any) {
      toast.error(error.response.data[0].non_field_errors);
      reset();
    }
  };

  React.useEffect(() => {
    if (lunchFeedbackQuestionList.length) {
      const mulitpleAnswers = lunchFeedbackQuestionList
        .filter((data, index) => {
          return data.question_type == 'm';
        })
        .map((data, index) => {
          return {
            training: Questions?.[0].training_routine_details.training_details.id,
            routine_feedback_que_id: Questions?.[0]?.routine_feedback_que_id?.toString() ?? '',
            feedback_question_id: data.tms_feedback_question_id,
            feedback_point: '',
            feedback_date: date
          };
        });
      const shortAnswers = lunchFeedbackQuestionList
        .filter((data, index) => {
          return data.question_type == 's';
        })
        .map((data, index) => {
          return {
            training: Questions?.[0].training_routine_details.training_details.id,
            routine_feedback_que_id: Questions?.[0]?.routine_feedback_que_id?.toString() ?? '',
            feedback_question_id: data.tms_feedback_question_id,
            feedback_answer: '',
            feedback_date: date
          };
        });

      reset({ feedbackAnswers: [...mulitpleAnswers, ...shortAnswers] });
    }
  }, [lunchFeedbackQuestionList, Questions, routineId]);

  return (
    <div>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        open={openFeedbackModal}
        onClose={handleCloseFeedbackModal}>
        <DialogTitle id="alert-dialog-title">
          <Typography variant="h4" gutterBottom>
            {Questions?.[0]?.training_routine_details?.training_details?.training_name}
          </Typography>{' '}
        </DialogTitle>
        {lunchFeedbackQuestionList?.length > 0 ? (
          <DialogContent>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
              <Grid container columnSpacing={4}>
                <Grid item xs={5}></Grid>
                <Grid item xs={7} sx={{ float: 'right' }}>
                  <Stack direction={'row'} display={'flex'} justifyContent={'center'}>
                    <i>In the given likert scale number 1 indicates poor and 4 indicates best.</i>
                  </Stack>
                  <Stack direction={'row'} justifyContent={'space-around'}>
                    {[...Array(4)].map((item, index) => (
                      <>
                        <Typography>{index + 1}</Typography>
                      </>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
              {lunchFeedbackQuestionList
                .filter((q) => q.question_type === 'm')
                .map((item, index) => {
                  return (
                    <RadioGroup
                      key={item.tms_feedback_question_id}
                      item={item}
                      selectedValue={
                        feedbackAns.find(
                          (a) => a.feedback_question_id === item.tms_feedback_question_id
                        )?.feedback_point ?? ''
                      }
                      onChange={(value) => {
                        setValue(
                          'feedbackAnswers',
                          feedbackAns.map((a, inde) => ({
                            ...a,
                            feedback_point:
                              a.feedback_question_id === item.tms_feedback_question_id
                                ? value
                                : a.feedback_point
                          }))
                        );
                      }}
                    />
                  );
                })}
              <hr />
              {lunchFeedbackQuestionList
                .filter((q) => q.question_type === 's')
                .map((item) => {
                  return (
                    <>
                      <Grid container justifyContent={'space-between'}>
                        <Grid xs={6}>
                          <Typography>{item?.question}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <UserTextInput
                            label={'Answer the question'}
                            type="text"
                            name="dis"
                            control={control}
                            sx={{ height: '50px' }}
                            value={
                              feedbackAns.find(
                                (a) => a.feedback_question_id === item.tms_feedback_question_id
                              )?.feedback_answer ?? ''
                            }
                            onChange={({ target: { value } }) => {
                              setValue(
                                'feedbackAnswers',
                                feedbackAns.map((a: any) => ({
                                  ...a,
                                  feedback_answer:
                                    a.feedback_question_id === item.tms_feedback_question_id
                                      ? value
                                      : a.feedback_answer
                                }))
                              );
                            }}
                          />
                        </Grid>
                      </Grid>
                    </>
                  );
                })}

              <hr />
              <>
                <Button
                  variant="outlined"
                  onClick={handleCloseFeedbackModal}
                  sx={{ marginRight: '8px' }}>
                  Cancel
                </Button>
                <Button variant="contained" autoFocus type="submit" disabled={isLoading}>
                  {isLoading ? "Submitting.." : "Submit"}
                </Button>
              </>
            </form>
          </DialogContent>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px'
            }}>
            <h4>No Questions here</h4>
          </Box>
        )}
      </Dialog>
    </div>
  );
};
export default LunchFeedbackModal;
